import { FormattedMessage, useIntl } from "react-intl";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAppContext } from "../app/AppContext";
import { BrandMark } from "../shared/Shared";
import styles from "./PublicShell.module.css";
import mfaStyles from "./PublicShellMfa.module.css";

/**
 * Signed-out shell. The header forked with the 2026-07-26 MFA splash redesign:
 * MFA carries the prototype nav (How it works / Templates / For parents /
 * Pricing section-and-page links plus ghost "Log in" and violet "Start free"
 * pills, styled by PublicShellMfa.module.css); LYA keeps the original V1-port
 * chrome (Templates, plain Sign in, Start free) untouched. The footer forked
 * the same way (2026-07-27): MFA carries the prototype footer — brand +
 * tagline, link nav, copyright — while LYA keeps the one-line trust strip.
 * The locale switch renders on both sites, and member chrome stays in
 * AppShell — this shell never shows authenticated affordances.
 */
export function PublicShell() {
  const intl = useIntl();
  const { pathname } = useLocation();
  const { experience, locale, setLocale } = useAppContext();
  const isMfa = experience.id === "myfirstagent";
  // The MFA splash owns its own section widths and needs its dark bands
  // full-bleed; every other public page keeps the shared gutters.
  const fullBleed = isMfa && pathname === "/";
  // The unified template gallery owns its own width/padding (it renders inside
  // the member AppShell too); the shell's gutters would shrink it on narrow
  // viewports, so /templates drops them on both brands.
  const bareMain = pathname === "/templates";
  const localeSelect = (
    <select
      className={styles.locale}
      aria-label={intl.formatMessage({ id: "common.language" })}
      value={locale}
      onChange={(event) => setLocale(event.target.value as "en" | "es")}
    >
      <option value="en">English</option>
      <option value="es">Español</option>
    </select>
  );
  return (
    <div className={styles.shell}>
      <header className={isMfa ? `${styles.header} ${mfaStyles.header}` : styles.header}>
        <Link to="/" className={isMfa ? `${styles.brand} ${mfaStyles.brand}` : styles.brand}>
          <BrandMark label={experience.brand.name} />
          {experience.brand.name}
        </Link>
        {isMfa ? (
          <>
            <nav className={mfaStyles.nav} aria-label={intl.formatMessage({ id: "nav.primary" })}>
              {/* Section links target the splash anchors (MfaSplashPage owns
                  the ids and the hash-scroll effect); Templates and Pricing
                  are real pages. Header "Start free" stays /register — only
                  the splash hero field and chips enter the Step-1 handoff. */}
              <Link to="/#how" className={mfaStyles.navLink}>
                <FormattedMessage id="mfaSplash.nav.how" />
              </Link>
              <NavLink to="/templates" className={mfaStyles.navLink}>
                <FormattedMessage id="publicNav.templates" />
              </NavLink>
              <Link to="/pricing" className={mfaStyles.navLink}>
                <FormattedMessage id="mfaSplash.nav.pricing" />
              </Link>
              {localeSelect}
            </nav>
            <div className={mfaStyles.pills}>
              <Link to="/login" className={mfaStyles.pillGhost}>
                <FormattedMessage id="mfaSplash.nav.logIn" />
              </Link>
              <Link to="/register" className={mfaStyles.pillPrimary}>
                <FormattedMessage id="publicNav.startFree" />
              </Link>
            </div>
          </>
        ) : (
          <nav className={styles.nav} aria-label={intl.formatMessage({ id: "nav.primary" })}>
            <NavLink to="/templates" className={styles.navLink}>
              <FormattedMessage id="publicNav.templates" />
            </NavLink>
            {localeSelect}
            {/* LYA keeps the original owner-canvas signed-out nav: plain Sign
                in, Start free pill (pricing is reached from the splash CTA). */}
            <Link to="/login" className={styles.navLink}>
              <FormattedMessage id="publicNav.signIn" />
            </Link>
            <Link to="/register" className={styles.startFree}>
              <FormattedMessage id="publicNav.startFree" />
            </Link>
          </nav>
        )}
      </header>
      <main
        className={
          fullBleed
            ? `${styles.main} ${mfaStyles.mainBleed}`
            : bareMain
              ? `${styles.main} ${styles.mainBare}`
              : styles.main
        }
      >
        <Outlet />
      </main>
      {isMfa ? (
        <footer className={mfaStyles.footer}>
          <div className={mfaStyles.footerBrand}>
            <BrandMark label={experience.brand.name} />
            {experience.brand.name}
          </div>
          <span className={mfaStyles.footerTagline}>
            <FormattedMessage id="mfaSplash.footer.tagline" />
          </span>
          <nav
            className={mfaStyles.footerNav}
            aria-label={intl.formatMessage({ id: "mfaSplash.footer.navLabel" })}
          >
            <Link to="/pricing" className={mfaStyles.footerLink}>
              <FormattedMessage id="mfaSplash.nav.pricing" />
            </Link>
            <Link to="/privacy" className={mfaStyles.footerLink}>
              <FormattedMessage id="mfaSplash.footer.privacy" />
            </Link>
            <Link to="/terms" className={mfaStyles.footerLink}>
              <FormattedMessage id="mfaSplash.footer.terms" />
            </Link>
            <span className={mfaStyles.footerCopyright}>
              <FormattedMessage id="mfaSplash.footer.copyright" />
            </span>
          </nav>
        </footer>
      ) : (
        <footer className={styles.footer}>
          <FormattedMessage id="splash.trust.lya" />
          <nav
            className={styles.footerNav}
            aria-label={intl.formatMessage({ id: "mfaSplash.footer.navLabel" })}
          >
            <Link to="/privacy" className={styles.footerLink}>
              <FormattedMessage id="mfaSplash.footer.privacy" />
            </Link>
            <Link to="/terms" className={styles.footerLink}>
              <FormattedMessage id="mfaSplash.footer.terms" />
            </Link>
            <span>
              <FormattedMessage id="mfaSplash.footer.copyright" />
            </span>
          </nav>
        </footer>
      )}
    </div>
  );
}
